package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.DuplicateNearbyBanner
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.wizard_choice_hint
import fr.frux.feature.treeadd.resources.wizard_choice_site_hint
import fr.frux.feature.treeadd.resources.wizard_choice_site_title
import fr.frux.feature.treeadd.resources.wizard_choice_title
import fr.frux.feature.treeadd.resources.wizard_choice_tree_hint
import fr.frux.feature.treeadd.resources.wizard_choice_tree_title
import fr.frux.feature.treeadd.resources.wizard_duplicate_confirm
import fr.frux.feature.treeadd.resources.wizard_duplicate_confirm_expanded
import fr.frux.feature.treeadd.resources.wizard_duplicate_subtitle
import fr.frux.feature.treeadd.resources.wizard_duplicate_title
import fr.frux.feature.treeadd.resources.wizard_place_header_expanded
import fr.frux.feature.treeadd.resources.wizard_place_hint_expanded
import fr.frux.feature.treeadd.resources.wizard_place_tip_1
import fr.frux.feature.treeadd.resources.wizard_place_tip_2
import fr.frux.feature.treeadd.resources.wizard_place_tip_3
import fr.frux.shared.domain.model.DuplicateCandidate
import org.jetbrains.compose.resources.stringResource

private val SHEET_CORNER = 28.dp
private val TILE_CORNER = 20.dp

/**
 * Étape 1 sur téléphone : overlay transparent au-dessus de la carte persistante en mode visée.
 * Ni `Surface` ni `Scaffold` plein écran (ils absorbent les gestes de la carte) : seuls l'en-tête,
 * le bandeau et la feuille basse captent le pointeur.
 */
@Composable
internal fun PlaceStepCompact(
    candidate: DuplicateCandidate?,
    locateButton: @Composable () -> Unit,
    onTree: () -> Unit,
    onSite: () -> Unit,
    onCancel: () -> Unit,
    onOpenCandidate: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(modifier = modifier.fillMaxSize()) {
        Column(modifier = Modifier.align(Alignment.TopCenter)) {
            WizardHeader(step = WizardStep.PLACE, onBack = {}, onCancel = onCancel)
            if (candidate != null) {
                WizardDuplicateBanner(
                    candidate = candidate,
                    stacked = false,
                    onOpen = onOpenCandidate,
                    modifier = Modifier.padding(12.dp),
                )
            }
        }
        Column(modifier = Modifier.align(Alignment.BottomCenter)) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(end = 16.dp, bottom = 12.dp),
                contentAlignment = Alignment.CenterEnd,
            ) { locateButton() }
            PlaceSheet(onTree, onSite)
        }
    }
}

@Composable
private fun PlaceSheet(
    onTree: () -> Unit,
    onSite: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(topStart = SHEET_CORNER, topEnd = SHEET_CORNER),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 4.dp,
    ) {
        Column(
            modifier =
                Modifier.navigationBarsPadding().padding(
                    start = 20.dp,
                    top = 20.dp,
                    end = 20.dp,
                    bottom = 28.dp,
                ),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text(stringResource(Res.string.wizard_choice_title), style = MaterialTheme.typography.headlineSmall)
            Text(
                text = stringResource(Res.string.wizard_choice_hint),
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(Modifier.size(8.dp))
            TreeOrSiteTiles(onTree, onSite)
        }
    }
}

/** Étape 1 en grand écran : la colonne de gauche, la carte de droite sert de viseur. */
@Composable
internal fun PlaceStepExpanded(
    candidate: DuplicateCandidate?,
    onTree: () -> Unit,
    onSite: () -> Unit,
    onCancel: () -> Unit,
    onOpenCandidate: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    // Zone défilante puis choix ancré en bas, hors du scroll : les tuiles restent atteignables (#629).
    Column(modifier = modifier.fillMaxHeight().windowInsetsPadding(WindowInsets.statusBars)) {
        Column(
            modifier =
                Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(start = 24.dp, top = 24.dp, end = 24.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            PlaceStepExpandedBody(candidate, onCancel, onOpenCandidate)
        }
        Column(
            modifier = Modifier.padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(
                text = stringResource(Res.string.wizard_choice_hint),
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            TreeOrSiteTiles(onTree, onSite)
        }
    }
}

@Composable
private fun ColumnScope.PlaceStepExpandedBody(
    candidate: DuplicateCandidate?,
    onCancel: () -> Unit,
    onOpenCandidate: (Long) -> Unit,
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(
            text = stringResource(Res.string.wizard_place_header_expanded),
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.weight(1f),
        )
        CancelButton(onCancel)
    }
    WizardStepIndicator(WizardStep.PLACE)
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(stringResource(Res.string.wizard_choice_title), style = MaterialTheme.typography.headlineLarge)
        Text(stringResource(Res.string.wizard_place_hint_expanded), style = MaterialTheme.typography.bodyLarge)
    }
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        PlaceTip(1, stringResource(Res.string.wizard_place_tip_1))
        PlaceTip(2, stringResource(Res.string.wizard_place_tip_2))
        PlaceTip(3, stringResource(Res.string.wizard_place_tip_3))
    }
    if (candidate != null) {
        WizardDuplicateBanner(candidate = candidate, stacked = true, onOpen = onOpenCandidate)
    }
}

/**
 * « Un arbre » garde le geste de l'ancien « Suivant » (position figée, étape Espèce). « Un lieu »
 * remet la position visée à composeApp, qui ouvre la création de lieu.
 */
@Composable
internal fun TreeOrSiteTiles(
    onTree: () -> Unit,
    onSite: () -> Unit,
) {
    // Hauteur intrinsèque : l'indice du lieu tient sur deux lignes, les deux tuiles restent égales.
    Row(
        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        ChoiceTile(
            title = stringResource(Res.string.wizard_choice_tree_title),
            hint = stringResource(Res.string.wizard_choice_tree_hint),
            glyph = FruxSpeciesGlyphs.Pomme,
            tint = MaterialTheme.fruxColors.familyPomme,
            onClick = onTree,
            modifier = Modifier.weight(1f).fillMaxHeight(),
        )
        ChoiceTile(
            title = stringResource(Res.string.wizard_choice_site_title),
            hint = stringResource(Res.string.wizard_choice_site_hint),
            glyph = FruxSpeciesGlyphs.Arbre,
            tint = MaterialTheme.colorScheme.primary,
            onClick = onSite,
            modifier = Modifier.weight(1f).fillMaxHeight(),
        )
    }
}

@Composable
private fun ChoiceTile(
    title: String,
    hint: String,
    glyph: ImageVector,
    tint: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        // Description qui commence par le titre : l'E2E et TalkBack trouvent la tuile par « Un arbre ».
        modifier =
            modifier.heightIn(min = 128.dp).semantics(mergeDescendants = true) {
                contentDescription = "$title, $hint"
                role = Role.Button
            },
        shape = RoundedCornerShape(TILE_CORNER),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            TreeGlyphDisc(glyph = glyph, tint = tint, size = 44.dp)
            Text(title, style = MaterialTheme.typography.titleMedium)
            Text(
                text = hint,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun PlaceTip(
    number: Int,
    text: String,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
        Box(
            modifier = Modifier.size(28.dp).background(MaterialTheme.colorScheme.primaryContainer, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = number.toString(),
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.onPrimaryContainer,
            )
        }
        Text(text, style = MaterialTheme.typography.bodyLarge, modifier = Modifier.weight(1f))
    }
}

@Composable
private fun WizardDuplicateBanner(
    candidate: DuplicateCandidate,
    stacked: Boolean,
    onOpen: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    val species = candidate.tree.species
    DuplicateNearbyBanner(
        glyph = speciesGlyph(species.scientificName, null),
        tint = speciesTint(species.scientificName),
        title = stringResource(Res.string.wizard_duplicate_title, species.commonNameFr, candidate.roundedMeters),
        subtitle = stringResource(Res.string.wizard_duplicate_subtitle),
        confirmLabel =
            if (stacked) {
                stringResource(Res.string.wizard_duplicate_confirm_expanded)
            } else {
                stringResource(Res.string.wizard_duplicate_confirm)
            },
        onConfirm = { onOpen(candidate.tree.id) },
        modifier = modifier,
        stacked = stacked,
    )
}
